import {Fragment} from 'react';
import {Link, useLocation} from 'react-router';
import {useAuth} from '@common/auth/use-auth';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {getBlocksForArea, type BlockInstance} from './appearance-data';
import {BLOCK_COMPONENTS} from './blocks/registry';

/**
 * Re-resolve a block's props for the given locale on the client, so switching
 * language updates block content live (no page reload). Falls back to the
 * server-resolved `props`, then English, then settings-only.
 */
function resolveBlockProps(block: BlockInstance, locale: string): Record<string, any> {
  const t = block.translations;
  if (!t || typeof t !== 'object') return block.props;
  const settings = (block.settings && typeof block.settings === 'object') ? block.settings : {};
  const localized = t[locale] ?? t['en'] ?? null;
  if (!localized) return block.props;
  return {...settings, ...localized};
}

/**
 * Renders all visible blocks of an area from the active theme, in order. This is
 * the SPA-native dynamic frontend rendering: drop <BlockArea area="homepage" />
 * anywhere and the admin-configured blocks appear. Unknown block types are
 * skipped (forward-compatible with blocks added on the backend first).
 */
/** True when the page is rendered inside the admin Live Editor's preview iframe. */
export function inLiveEditor(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.location.search.includes('mtk_editor=1') &&
    window.self !== window.top
  );
}

export function BlockArea({area}: {area: string}) {
  const blocks = getBlocksForArea(area);
  // Subscribes to the active locale so a language switch re-renders + re-resolves
  // every block's props live (the user-visible fix for "text doesn't change
  // until I refresh the page").
  const {localeCode} = useSelectedLocale();
  const {hasPermission} = useAuth();
  const {pathname} = useLocation();
  const editor = inLiveEditor();
  // Inline "edit this section" affordance on the REAL site (not the editor
  // iframe, not the back-office) for admins — click any block to jump straight
  // into the Live Editor focused on it.
  const adminEdit =
    !editor &&
    hasPermission('admin') &&
    !/^\/(admin|dashboard|student|company)\b/.test(pathname);
  if (!blocks.length) return null;

  return (
    <Fragment>
      {blocks.map((block, blockIndex) => {
        const Component = BLOCK_COMPONENTS[block.type];
        if (!Component) return null;
        // `__index` lets a block alternate its background with its neighbours
        // (see section-kit's `surface()`), so the page doesn't read as one flat
        // white column. The wrappers are display:contents, so CSS nth-child
        // can't do this for us.
        const props = {...resolveBlockProps(block, localeCode), __index: blockIndex};
        // Wrap each block in a tagged div so the Effects CSS gates can match
        // by block type (e.g. .ap-no-announcements [data-block-type='announcement']).
        // display:contents keeps the wrapper out of the layout tree so block
        // styles like full-bleed backgrounds keep working.
        if (editor) {
          // Live-editor preview: overlay captures the click and notifies the
          // parent editor so it opens this block's settings.
          return (
            <div
              key={block.id}
              data-block-type={block.type}
              data-block-id={block.id}
              className="group/mtk relative"
            >
              <Component block={block} props={props} />
              <div
                role="button"
                title={block.type}
                onClick={() =>
                  window.parent.postMessage(
                    {type: 'mtk-block-click', id: block.id, area, blockType: block.type},
                    '*',
                  )
                }
                className="absolute inset-0 z-40 cursor-pointer transition hover:bg-primary/5 hover:ring-2 hover:ring-inset hover:ring-primary/70"
              >
                <span className="absolute left-8 top-8 hidden rounded bg-primary px-8 py-2 text-xs font-semibold text-white group-hover/mtk:inline">
                  {block.type}
                </span>
              </div>
            </div>
          );
        }
        if (adminEdit) {
          // Real site, admin viewer: hover reveals an "Edit" pill that deep-links
          // into the Live Editor with this block pre-selected.
          return (
            <div
              key={block.id}
              data-block-type={block.type}
              className="group/mtkedit relative"
            >
              <Component block={block} props={props} />
              <Link
                to={`/admin/appearance/live?focus=${area}:${block.id}`}
                className="absolute right-12 top-12 z-40 hidden items-center gap-4 rounded-full bg-primary px-10 py-4 text-xs font-semibold text-white shadow-lg group-hover/mtkedit:inline-flex hover:bg-primary-dark"
              >
                <span className="material-icons text-sm">edit</span>
                Edit section
              </Link>
              <span className="pointer-events-none absolute inset-0 z-30 hidden rounded ring-2 ring-inset ring-primary/40 group-hover/mtkedit:block" />
            </div>
          );
        }
        return (
          <div
            key={block.id}
            data-block-type={block.type}
            style={{display: 'contents'}}
          >
            <Component block={block} props={props} />
          </div>
        );
      })}
    </Fragment>
  );
}

/** True when the active theme has at least one visible block in the area. */
export function hasBlocks(area: string): boolean {
  return getBlocksForArea(area).length > 0;
}

/**
 * Renders the `header` block-area (e.g. the announcement / campaign bar) at the
 * very top of every public-facing page, so a Live-Editor edit to a Header block
 * reflects site-wide. Hidden in the back-office (admin/dashboard/student).
 */
export function SiteHeaderBlocks() {
  const {pathname} = useLocation();
  // Back-office and the portal shells (student/company) render their own header
  // block-area inside their h-screen layout, so skip the root-level one here to
  // avoid a duplicate / an overflow above the fixed-height grid.
  if (/^\/(admin|dashboard|student|company)\b/.test(pathname)) return null;
  if (!hasBlocks('header')) return null;
  return (
    <div className="mtk-site-header-blocks">
      <BlockArea area="header" />
    </div>
  );
}
